/**
 * Activity Admin Page
 * 
 * Admin-only page showing household activity logs with filtering and pagination
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFamilyAuth } from '../../../contexts/FamilyAuthContext';
import { fetchActivityLogs } from '../../../api/activityApi';

// Category configurations
const CATEGORIES = [
  { id: 'all', label: 'Alle', color: 'neutral' },
  { id: 'senior', label: 'Senioren', color: 'purple' },
  { id: 'reminder', label: 'Erinnerungen', color: 'blue' },
  { id: 'device', label: 'Geräte', color: 'green' },
  { id: 'admin', label: 'Admin', color: 'orange' },
  { id: 'auth', label: 'Login/Logout', color: 'cyan' },
  { id: 'system', label: 'System', color: 'gray' }
];

// Actor type badges
const ACTOR_TYPE_COLORS = {
  family_user: 'bg-[#5B4B8A] text-white',
  device: 'bg-[#6DCEE8] text-white',
  system: 'bg-neutral-600 text-white'
};

// Category badge colors
const CATEGORY_COLORS = {
  senior: 'bg-purple-500/20 text-purple-200 border-purple-500/40',
  reminder: 'bg-blue-500/20 text-blue-200 border-blue-500/40',
  device: 'bg-green-500/20 text-green-200 border-green-500/40',
  admin: 'bg-orange-500/20 text-orange-200 border-orange-500/40',
  auth: 'bg-cyan-500/20 text-cyan-200 border-cyan-500/40',
  system: 'bg-neutral-500/20 text-neutral-200 border-neutral-500/40'
};

function ActivityAdminPage() {
  const { household, isAdmin } = useFamilyAuth();
  const navigate = useNavigate();
  
  // State
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [loadingMore, setLoadingMore] = useState(false);
  
  // Filters
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [limit, setLimit] = useState(50);
  const [skip, setSkip] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  // Redirect non-admins
  useEffect(() => {
    if (!isAdmin) {
      navigate('/family/dashboard');
    }
  }, [isAdmin, navigate]);

  // Load activities
  useEffect(() => {
    if (!household?.id) return;
    loadActivities(false);
  }, [household?.id, selectedCategory, limit]);

  const loadActivities = async (loadMore = false) => {
    if (!household?.id) return;
    
    try {
      if (loadMore) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        setActivities([]);
        setSkip(0);
      }
      
      const currentSkip = loadMore ? skip : 0;
      
      const data = await fetchActivityLogs(household.id, {
        category: selectedCategory,
        limit,
        skip: currentSkip
      });
      
      const newActivities = data.activities || [];
      
      if (loadMore) {
        setActivities(prev => [...prev, ...newActivities]);
        setSkip(currentSkip + newActivities.length);
      } else {
        setActivities(newActivities);
        setSkip(newActivities.length);
      }
      
      // Check if there are more activities
      setHasMore(newActivities.length === limit);
      setError('');
      
    } catch (err) {
      console.error('Failed to load activities:', err);
      setError('Aktivitätsdaten konnten nicht geladen werden. Bitte später erneut versuchen.');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleLoadMore = () => {
    loadActivities(true);
  };

  const formatTimestamp = (timestamp) => {
    try {
      const date = new Date(timestamp);
      return date.toLocaleString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return timestamp;
    }
  };

  const formatMetadata = (metadata) => {
    if (!metadata || typeof metadata !== 'object') return null;
    
    const entries = Object.entries(metadata).slice(0, 2);
    if (entries.length === 0) return null;
    
    return entries.map(([key, value]) => {
      let displayValue = value;
      
      // Format file size
      if (key === 'file_size' && typeof value === 'number') {
        displayValue = `${(value / 1024).toFixed(0)} KB`;
      }
      
      // Format role changes
      if (key === 'old_role' || key === 'new_role') {
        displayValue = value === 'admin' ? 'Admin' : 'Member';
      }
      
      return `${key}: ${displayValue}`;
    }).join(' | ');
  };

  if (!household?.id) {
    return (
      <div className="min-h-screen bg-[#050816] text-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#5B4B8A] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-neutral-400">Lade Haushaltsdaten...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050816] text-white">
      {/* Header */}
      <div className="bg-[#0D1124]/90 backdrop-blur-xl border-b border-neutral-600/40 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/family/dashboard')}
              className="p-2 hover:bg-neutral-700/50 rounded-lg transition-colors"
            >
              ← Zurück
            </button>
            <div className="flex-1">
              <h1 className="text-2xl font-bold">Aktivitätsbericht</h1>
              <p className="text-sm text-neutral-400">
                Alle Aktivitäten in {household.name || 'diesem Haushalt'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto p-6">
        {/* Filters */}
        <div className="bg-[#0D1124]/80 backdrop-blur-xl rounded-2xl border border-neutral-600/40 p-6 mb-6">
          {/* Category Filter */}
          <div className="mb-4">
            <label className="block text-sm font-medium mb-3">Kategorie</label>
            <div className="flex flex-wrap gap-2">
              {CATEGORIES.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.id)}
                  className={`px-4 py-2 rounded-lg font-medium transition-all ${
                    selectedCategory === cat.id
                      ? 'bg-gradient-to-r from-[#5B4B8A] to-[#6DCEE8] text-white'
                      : 'bg-neutral-700/50 hover:bg-neutral-700/70 text-neutral-300'
                  }`}
                >
                  {cat.label}
                </button>
              ))}
            </div>
          </div>

          {/* Limit Selector */}
          <div>
            <label className="block text-sm font-medium mb-3">Anzahl pro Seite</label>
            <div className="flex gap-2">
              {[20, 50, 100].map((l) => (
                <button
                  key={l}
                  onClick={() => setLimit(l)}
                  className={`px-4 py-2 rounded-lg font-medium transition-all ${
                    limit === l
                      ? 'bg-gradient-to-r from-[#5B4B8A] to-[#6DCEE8] text-white'
                      : 'bg-neutral-700/50 hover:bg-neutral-700/70 text-neutral-300'
                  }`}
                >
                  {l}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-500/20 border border-red-500/40 rounded-xl text-red-200">
            {error}
          </div>
        )}

        {/* Activity List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="w-12 h-12 border-4 border-[#5B4B8A] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-neutral-400">Lade Aktivitäten...</p>
          </div>
        ) : activities.length === 0 ? (
          <div className="bg-[#0D1124]/80 backdrop-blur-xl rounded-2xl border border-neutral-600/40 p-12 text-center">
            <div className="text-6xl mb-4">📊</div>
            <h3 className="text-xl font-semibold mb-2">Keine Aktivitäten</h3>
            <p className="text-neutral-400">
              Noch keine Aktivitäten für diesen Haushalt vorhanden.
            </p>
          </div>
        ) : (
          <>
            {/* Activities Table */}
            <div className="bg-[#0D1124]/80 backdrop-blur-xl rounded-2xl border border-neutral-600/40 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-neutral-700/30 border-b border-neutral-600/40">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-neutral-300 uppercase tracking-wider">
                        Zeitpunkt
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-neutral-300 uppercase tracking-wider">
                        Akteur
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-neutral-300 uppercase tracking-wider">
                        Kategorie
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-neutral-300 uppercase tracking-wider">
                        Aktion
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-neutral-300 uppercase tracking-wider">
                        Ziel
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-neutral-300 uppercase tracking-wider">
                        Details
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-600/40">
                    {activities.map((activity, idx) => {
                      // Handle both old and new activity log structures
                      const isOldFormat = !activity.actor && activity.type;
                      
                      // For old format, try to extract meaningful info from payload
                      let actor, category, action, target, metadata;
                      
                      if (isOldFormat) {
                        // Old format handling
                        const payload = activity.payload || {};
                        actor = {
                          name: payload.device_id ? `Gerät ${payload.device_id.slice(-3)}` : 'System',
                          type: payload.device_id ? 'device' : 'system'
                        };
                        category = activity.type?.includes('call') ? 'device' : 'system';
                        action = activity.type || '-';
                        target = null;
                        metadata = payload;
                      } else {
                        // New format
                        actor = activity.actor || { name: 'Unknown', type: 'system' };
                        category = activity.category || 'system';
                        action = activity.action || '-';
                        target = activity.target;
                        metadata = activity.metadata;
                      }
                      
                      return (
                        <tr key={idx} className="hover:bg-neutral-700/20 transition-colors">
                          {/* Timestamp */}
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {formatTimestamp(activity.created_at)}
                          </td>
                          
                          {/* Actor */}
                          <td className="px-6 py-4">
                            <div className="flex flex-col gap-1">
                              <span className="text-sm font-medium">{actor.name}</span>
                              <span className={`text-xs px-2 py-0.5 rounded-full w-fit ${ACTOR_TYPE_COLORS[actor.type] || 'bg-neutral-600 text-white'}`}>
                                {actor.type === 'family_user' && '👤 Family'}
                                {actor.type === 'device' && '📱 Device'}
                                {actor.type === 'system' && '⚙️ System'}
                              </span>
                            </div>
                          </td>
                          
                          {/* Category */}
                          <td className="px-6 py-4">
                            <span className={`px-3 py-1 rounded-lg text-xs font-medium border ${CATEGORY_COLORS[category] || 'bg-neutral-500/20 text-neutral-200 border-neutral-500/40'}`}>
                              {category}
                            </span>
                          </td>
                          
                          {/* Action */}
                          <td className="px-6 py-4 text-sm">
                            {action}
                          </td>
                          
                          {/* Target */}
                          <td className="px-6 py-4 text-sm">
                            {target ? (
                              <div className="flex flex-col">
                                <span className="text-neutral-400 text-xs">{target.type}</span>
                                <span className="font-medium">{target.name}</span>
                              </div>
                            ) : (
                              <span className="text-neutral-500">-</span>
                            )}
                          </td>
                          
                          {/* Metadata */}
                          <td className="px-6 py-4 text-xs text-neutral-400">
                            {formatMetadata(metadata) || '-'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Load More Button */}
            {hasMore && (
              <div className="mt-6 text-center">
                <button
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                  className="px-8 py-3 bg-gradient-to-r from-[#5B4B8A] to-[#6DCEE8] hover:opacity-90 rounded-xl font-semibold transition-all disabled:opacity-50"
                >
                  {loadingMore ? (
                    <span className="flex items-center gap-2">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Lädt...
                    </span>
                  ) : (
                    'Mehr laden'
                  )}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default ActivityAdminPage;
